<template>
  <div class="choose-photos-step">
    <h1 class="global-h1">{{ allPhotos.length ? "Choose photos" : "Upload some Photos" }}</h1>

    <div v-if="allPhotos?.length" style="margin-top: 10px; margin-bottom: 30px">
      <h5 style="margin-bottom: 10px">We'll include all {{ allPhotos.length }} of your existing uploads by default.</h5>
      <div class="media-files-row media-files-row--one-row-visible new-paid-creator-section__media-files-row">
        <div class="media-files-row__in-wrap">
          <div class="media-files-row__thumbnail-wrap" v-for="fileName in firstExamplePhotos" :key="'ex-' + fileName">
            <SrpFileThumbnail class="media-files-row__thumbnail" :fileName="fileName" :thumbSize="'thumb-med'" :isFixedAspectRatio="false" :isEntireAreaClickable="false" />
          </div>
        </div>
      </div>
    </div>

    <div style="margin-top: 20px; margin-bottom: 30px">
      <h5 style="margin-bottom: 10px">{{ allPhotos.length ? "You can always add more photos and videos!" : "We recommend 20+ photos for best results" }}</h5>
      <UploadPhotosNonAdventure :customerId="customerId" @imageAddedToGallery="addImageToNonAdventureGallery" flow="customer-upload" />
    </div>

    <div v-if="allPhotos.length" class="choose-photos-step__or-divider"></div>

    <div v-if="!allPhotos" class="social-post-gen-from-photos__placeholder">Loading photos...</div>
    <div v-else-if="allPhotos.length" style="margin-top: 30px; margin-bottom: 20px">
      <PhotosSpoiler
        class="all-photos-with-grouping__photos-spoiler"
        title="Select a subset of photos"
        :photosList="allPhotos"
        :isThumbnailsWithDownloadButton="false"
        :isThumbnailsWithCheckboxes="true"
        :checkedFileIds="_checkedFileIds"
        :index="-1"
        @checkUncheckFile="handleCheckUncheckFile"
        @checkUncheckAll="handleCheckUncheckAll"
        :isThumbnailsEntireAreaClickable="true"
        :isThumbnailsEntireAreaCheckable="true"
        :isWithSelectAllCheckbox="false"
        :isMyUploads="false"
        rootContainerQueryForCulling=".srp-modal__content-wrap"
        :defaultOpened="false"
        :customerId="props.customerId"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from "vue";
import { useRouter } from "vue-router";

// Components
import PhotosSpoiler from "@components/AllPhotosSection/PhotosSpoiler.vue";
import UploadPhotosNonAdventure from "@views/CMS/UploadPhotosNonAdventure.vue";
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";

const router = useRouter();

const props = withDefaults(
  defineProps<{
    customerId: string;
    allPhotos: Array<string>;
    checkedFileIds: Array<string>;
    checkedFilesLimit: number;
  }>(),
  {
    customerId: "",
    allPhotos: () => [],
    checkedFileIds: () => [],
    checkedFilesLimit: 250,
  }
);

const emit = defineEmits<{
  (e: "checkUncheckFile", fileName: string, isChecked: boolean): void;
  (e: "checkUncheckAll", photos: string[], isChecked: boolean): void;
  (e: "imageAddedToGallery", imageId: string): void;
  (e: "update:checkedFileIds", value: Array<string>): void;
}>();

// Checked files ==============================================================
const _checkedFileIds = ref<string[]>([]);

watch(_checkedFileIds, () => emit("update:checkedFileIds", _checkedFileIds.value));

const handleCheckUncheckFile = (fileName: string, isChecked: boolean) => {
  if (isChecked) {
    _checkedFileIds.value = [..._checkedFileIds.value, fileName];
  } else {
    _checkedFileIds.value = _checkedFileIds.value.filter(f => f !== fileName);
  }
  emit("checkUncheckFile", fileName, isChecked);
};

const handleCheckUncheckAll = (isChecked: boolean) => {
  _checkedFileIds.value = isChecked ? [...props.allPhotos] : [];
  emit("checkUncheckAll", props.allPhotos, isChecked);
};

const addImageToNonAdventureGallery = (imageId: string) => {
  emit("imageAddedToGallery", imageId);
};

const firstExamplePhotos = computed(() => {
  return props.allPhotos?.slice(0, 20);
});
</script>

<style scoped lang="scss">
.choose-photos-step {
  &__or-divider {
    width: 100%;
    height: 40px;
    margin-bottom: 5px;
    position: relative;
    z-index: 0;

    &::before {
      content: "";
      width: 100%;
      height: 0;
      border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
      position: absolute;
      inset: 50% auto auto 0;
    }

    &::after {
      content: "or";
      width: 41px;
      height: 41px;
      padding: 0 0 2px;
      box-sizing: border-box;
      border: 1px rgba(0, 0, 0, 0.2) solid;
      border-radius: 1000px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      inset: 0 auto auto calc(50% - 20px);
      color: rgba(0, 0, 0, 1);
      font:
        600 19px/19px "Quicksand",
        sans-serif;
      background: white; // rgb(243, 243, 243);
    }
  }
}

.media-files-row {
  height: 87px;
  position: relative;
  overflow: hidden;

  &--one-row-visible {
    height: 70px;
  }

  &::after {
    content: "";
    width: 40px;
    height: 100%;
    position: absolute;
    inset: 0 0 auto auto;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
    pointer-events: none;
  }

  &__in-wrap {
    width: calc(100% + 5px);
    min-width: calc(100% + 5px);
    display: flex;
    flex-wrap: wrap;
  }

  &__thumbnail-wrap {
    flex-grow: 1;
    max-width: 70px;
    height: 70px;
    margin: 0 5px 5px 0;
    opacity: 1;
    transition: opacity 0.05s ease-in-out;

    :deep(.file-thumbnail__video-icon) {
      width: 25px;
      min-width: 25px;
      max-width: 25px;
      height: 25px;
    }
  }

  &__thumbnail {
    height: 100%;

    :deep(img) {
      border-radius: 3px !important;
    }
  }
}
</style>
